// src/components/Sidebar.jsx
import { useState, useEffect } from "react";
import { Crown, Package, Shirt, Pickaxe, ArrowRight, Users } from "lucide-react";
import { API } from "../utils/constants";
import { useServerStatus } from "../hooks/useServerStatus";

const DISCORD_SVG = (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="#818cf8">
    <path d="M20.317 4.37a19.791 19.791 0 0 0-4.885-1.515.074.074 0 0 0-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 0 0-5.487 0 12.64 12.64 0 0 0-.617-1.25.077.077 0 0 0-.079-.037A19.736 19.736 0 0 0 3.677 4.37a.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.057a.082.082 0 0 0 .031.057 19.9 19.9 0 0 0 5.993 3.03.078.078 0 0 0 .084-.028 14.09 14.09 0 0 0 1.226-1.994.076.076 0 0 0-.041-.106 13.107 13.107 0 0 1-1.872-.892.077.077 0 0 1-.008-.128 10.2 10.2 0 0 0 .372-.292.074.074 0 0 1 .077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 0 1 .078.01c.12.098.246.198.373.292a.077.077 0 0 1-.006.127 12.299 12.299 0 0 1-1.873.892.077.077 0 0 0-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.028 19.839 19.839 0 0 0 6.002-3.03.077.077 0 0 0 .032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 0 0-.031-.03zM8.02 15.33c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.956-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.956 2.418-2.157 2.418zm7.975 0c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.955-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.946 2.418-2.157 2.418z"/>
  </svg>
);

function relTime(d) {
  const s = Math.floor((Date.now() - new Date(d)) / 1000);
  if (s < 60) return "just now";
  const m = Math.floor(s / 60); if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60); if (h < 24) return `${h}h ago`;
  const dy = Math.floor(h / 24); if (dy < 30) return `${dy}d ago`;
  return new Date(d).toLocaleDateString("en-GB", { day: "2-digit", month: "short" });
}

export default function Sidebar({ showToast, onOpenMcStore, onOpenForum }) {
  const { discord, minecraft, loaded } = useServerStatus();
  const [recentThreads, setRecentThreads] = useState([]);
  const [threadsLoading, setThreadsLoading] = useState(true);

  useEffect(() => {
    fetch(`${API}/forum/recent?limit=3`)
      .then(r => r.json())
      .then(d => setRecentThreads(d.threads || []))
      .catch(() => setRecentThreads([]))
      .finally(() => setThreadsLoading(false));
  }, []);

  const [merchEmail,      setMerchEmail]      = useState(() => localStorage.getItem("toa_merch_email") || "");
  const [merchSubmitting, setMerchSubmitting] = useState(false);
  const [alreadySubmitted, setAlreadySubmitted] = useState(
    () => localStorage.getItem("toa_merch_submitted") === "true"
  );

  const handleMerchNotify = async () => {
    if (!merchEmail.includes("@") || merchSubmitting) return;
    setMerchSubmitting(true);
    try {
      const res  = await fetch(`${API}/merch/notify`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: merchEmail }),
      });
      const data = await res.json();
      showToast(data.message || "You're on the list!");
      localStorage.setItem("toa_merch_submitted", "true");
      localStorage.removeItem("toa_merch_email");
      setMerchEmail("");
      setAlreadySubmitted(true);
    } catch {
      showToast("Something went wrong, try again.");
    } finally {
      setMerchSubmitting(false);
    }
  };

  const mcOnline = loaded ? minecraft.online : null;
  const dcOnline = loaded ? discord.online   : null;

  const StatusDot = ({ online }) => (
    <span style={{
      display: "inline-block", width: 7, height: 7, borderRadius: "50%", marginRight: 5,
      background: online === null ? "#666" : online ? "#4ade80" : "#ef4444",
      boxShadow: online ? "0 0 6px #4ade80" : "none",
    }} />
  );

  return (
    <aside>
      {/* Status */}
      <div className="toa-sc">
        <div className="toa-st">
          TOA Status{" "}
          <div className="toa-sbadge" style={{
            background: mcOnline === null ? "rgba(100,100,100,.15)" : mcOnline ? "rgba(74,222,128,.12)" : "rgba(239,68,68,.12)",
            border: `1px solid ${mcOnline === null ? "rgba(100,100,100,.2)" : mcOnline ? "rgba(74,222,128,.25)" : "rgba(239,68,68,.25)"}`,
            color: mcOnline === null ? "#888" : mcOnline ? "#4ade80" : "#ef4444",
          }}>
            <StatusDot online={mcOnline} />
            {mcOnline === null ? "Checking..." : mcOnline ? "Live" : "Offline"}
          </div>
        </div>

        <div className="toa-srow">
          <div className="toa-srowl">
            <div className="toa-sico toa-id">{DISCORD_SVG}</div>
            <div><div className="toa-sname">Discord Server</div></div>
          </div>
          <span className="toa-badge toa-bb">
            <Users className="toa-ico-sm" />
            {loaded ? discord.memberCount.toLocaleString() : "..."}
          </span>
        </div>

        <div className="toa-srow">
          <div className="toa-srowl">
            <div className="toa-sico toa-im"><Pickaxe className="toa-ico" /></div>
            <div><div className="toa-sname">Minecraft</div></div>
          </div>
          <span className="toa-badge" style={{
            background: mcOnline ? "rgba(74,222,128,.1)" : "rgba(239,68,68,.1)",
            border: `1px solid ${mcOnline ? "rgba(74,222,128,.25)" : "rgba(239,68,68,.2)"}`,
            color: mcOnline ? "#4ade80" : "#ef4444",
          }}>
            <Users className="toa-ico-sm" />
            {loaded ? `${minecraft.playerCount}/${minecraft.maxPlayers}` : "..."}
          </span>
        </div>
      </div>

      {/* MC Store */}
      <div className="toa-sc">
        <div className="toa-st">TOA MC Store</div>
        <style>{`
          @keyframes scard-coin-float { 0%,100%{transform:translateY(0) rotate(-4deg)} 50%{transform:translateY(-6px) rotate(4deg)} }
          @keyframes scard-crown-float { 0%,100%{transform:translateY(0)} 50%{transform:translateY(-5px)} }
        `}</style>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gridTemplateRows: "auto auto", gap: 7, padding: 10 }}>
          <div className="toa-scard toa-pu" style={{ gridColumn: 1, gridRow: 1, padding: "10px 8px" }} onClick={() => onOpenMcStore?.("ranks")}>
            <div style={{ animation: "scard-crown-float 2.8s ease-in-out infinite", display: "inline-block" }}>
              <Crown style={{ width: 20, height: 20, color: "var(--purpleB)" }} />
            </div>
            <div className="toa-scard-n" style={{ fontSize: 9, marginTop: 5 }}>Ranks</div>
          </div>
          <div className="toa-scard toa-go" style={{ gridColumn: 1, gridRow: 2, padding: "10px 8px" }} onClick={() => onOpenMcStore?.("coins")}>
            <div style={{ animation: "scard-coin-float 2.5s ease-in-out infinite", display: "inline-block" }}>
              <img src="/toa-coin_mc.png" alt="TOA Coin" style={{ width: 22, height: 22, objectFit: "contain", display: "block", margin: "0 auto", filter: "drop-shadow(0 0 5px rgba(201,168,76,.55))" }} />
            </div>
            <div className="toa-scard-n" style={{ fontSize: 9, marginTop: 5 }}>TOA Coins</div>
          </div>
          <div className="toa-scard toa-cr" style={{ gridColumn: 2, gridRow: "1 / 3", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 6, padding: "10px 8px" }} onClick={() => showToast("Crates - coming soon!")}>
            <Package style={{ width: 22, height: 22, color: "#f87171" }} />
            <div className="toa-scard-n" style={{ fontSize: 9 }}>Crates</div>
          </div>
        </div>
      </div>

      {/* Merch */}
      <div className="toa-sc">
        <div className="toa-st">TOA Merch</div>
        <div style={{ padding: "16px 16px 18px" }}>
          <div style={{ display: "flex", justifyContent: "center", marginBottom: 8 }}>
            <Shirt className="toa-ico-lg" />
          </div>
          <div className="toa-cs-badge" style={{ display: "block", textAlign: "center", marginBottom: 8 }}>Coming Soon</div>
          <div style={{ fontSize: 12, color: "var(--muted)", lineHeight: 1.6, textAlign: "center", marginBottom: 10 }}>
            Official TOA clothing and merchandise. Rep the creed wherever you go.
          </div>
          {alreadySubmitted ? (
            <div style={{ fontSize: 12, color: "var(--gold)", textAlign: "center", padding: "8px 0", fontFamily: "'Cinzel',serif", letterSpacing: 1 }}>
              ✓ You're on the list!
            </div>
          ) : (
            <>
              <input
                type="email"
                placeholder="Enter your email..."
                value={merchEmail}
                onChange={e => { setMerchEmail(e.target.value); localStorage.setItem("toa_merch_email", e.target.value); }}
                onKeyDown={e => e.key === "Enter" && handleMerchNotify()}
                style={{ width: "100%", background: "rgba(255,255,255,.04)", border: "1px solid var(--borderB)", borderRadius: 6, padding: "8px 12px", fontSize: 12, color: "var(--text)", marginBottom: 8, outline: "none", fontFamily: "Inter,sans-serif", boxSizing: "border-box" }}
              />
              <button
                disabled={!merchEmail.includes("@") || merchSubmitting}
                onClick={handleMerchNotify}
                className="toa-btn toa-btn-g"
                style={{ width: "100%", fontSize: 11, padding: "9px 0", opacity: merchEmail.includes("@") && !merchSubmitting ? 1 : 0.4, cursor: merchEmail.includes("@") && !merchSubmitting ? "pointer" : "not-allowed" }}
              >
                {merchSubmitting ? "Saving..." : "Notify Me"}
              </button>
            </>
          )}
        </div>
      </div>

      {/* Forum */}
      <div className="toa-sc" id="forum">
        <div className="toa-st">Recent Threads</div>
        {threadsLoading && [1,2,3].map(i => (
          <div key={i} className="toa-thread" style={{ gap: 10 }}>
            <div style={{ width: 28, height: 28, borderRadius: 6, background: "rgba(155,127,212,.1)", flexShrink: 0 }} />
            <div style={{ flex: 1 }}>
              <div style={{ height: 9, borderRadius: 4, background: "rgba(255,255,255,.06)", marginBottom: 6, width: "70%" }} />
              <div style={{ height: 8, borderRadius: 4, background: "rgba(255,255,255,.04)", width: "45%" }} />
            </div>
          </div>
        ))}
        {!threadsLoading && recentThreads.length === 0 && (
          <div style={{ padding: "14px 16px", fontSize: 12, color: "var(--muted)", fontStyle: "italic" }}>No threads yet.</div>
        )}
        {!threadsLoading && recentThreads.map(t => (
          <button key={t.id} className="toa-thread" style={{ background: "none", border: "none", cursor: "pointer", textAlign: "left", width: "100%" }}
            onClick={() => onOpenForum?.(t.category_id, t.tag || null)}>
            <div className="toa-ti-avatar" style={{ background: "linear-gradient(135deg,#2a1650,#160a30)", border: "1px solid rgba(123,94,167,.25)", overflow: "hidden" }}>
              <svg viewBox="0 0 28 28" width="28" height="28" fill="none">
                <circle cx="14" cy="10.5" r="4.5" fill="rgba(155,127,212,0.55)" />
                <path d="M4 24c0-5.523 4.477-9 10-9s10 3.477 10 9" fill="rgba(155,127,212,0.35)" />
              </svg>
            </div>
            <div className="toa-ti-body">
              <span className="toa-ti-name" style={{ WebkitLineClamp: 2, display: "-webkit-box", WebkitBoxOrient: "vertical", overflow: "hidden" }}>{t.title}</span>
              <div className="toa-ti-meta">By: <span>{t.author}</span> · {t.created_at ? relTime(t.created_at) : ""}</div>
            </div>
          </button>
        ))}
        <div style={{ padding: "10px 16px", borderTop: "1px solid var(--border)", textAlign: "center" }}>
          <button className="toa-btn toa-btn-p" style={{ fontSize: 10, padding: "7px 16px", display: "inline-flex", alignItems: "center", gap: 5, cursor: "pointer" }}
            onClick={() => onOpenForum?.()}>
            View All <ArrowRight className="toa-ico-sm" />
          </button>
        </div>
      </div>

      {/* Social */}
      <div className="toa-sc">
        <div className="toa-st">Follow Us</div>
        <div style={{ padding: "14px 16px 16px", display: "flex", gap: 10, justifyContent: "center" }}>
          <a href="https://youtube.com/@theonesabovemedia" target="_blank" rel="noreferrer" title="YouTube"
            style={{ display: "flex", alignItems: "center", justifyContent: "center", flex: 1, padding: "12px 8px", borderRadius: 10, background: "rgba(255,0,0,.08)", border: "1px solid rgba(255,0,0,.2)", textDecoration: "none", transition: ".15s" }}
            onMouseEnter={e => { e.currentTarget.style.background = "rgba(255,0,0,.18)"; e.currentTarget.style.transform = "translateY(-2px)"; }}
            onMouseLeave={e => { e.currentTarget.style.background = "rgba(255,0,0,.08)"; e.currentTarget.style.transform = "translateY(0)"; }}>
            <svg width="22" height="22" viewBox="0 0 24 24" fill="#ff3333"><path d="M23.5 6.2a3 3 0 0 0-2.1-2.1C19.5 3.5 12 3.5 12 3.5s-7.5 0-9.4.6A3 3 0 0 0 .5 6.2C0 8.1 0 12 0 12s0 3.9.5 5.8a3 3 0 0 0 2.1 2.1c1.9.6 9.4.6 9.4.6s7.5 0 9.4-.6a3 3 0 0 0 2.1-2.1C24 15.9 24 12 24 12s0-3.9-.5-5.8zM9.7 15.5V8.5l6.3 3.5-6.3 3.5z"/></svg>
          </a>
          <a href="#" target="_blank" rel="noreferrer" title="X (Twitter)"
            style={{ display: "flex", alignItems: "center", justifyContent: "center", flex: 1, padding: "12px 8px", borderRadius: 10, background: "rgba(255,255,255,.05)", border: "1px solid rgba(255,255,255,.12)", textDecoration: "none", transition: ".15s" }}
            onMouseEnter={e => { e.currentTarget.style.background = "rgba(255,255,255,.12)"; e.currentTarget.style.transform = "translateY(-2px)"; }}
            onMouseLeave={e => { e.currentTarget.style.background = "rgba(255,255,255,.05)"; e.currentTarget.style.transform = "translateY(0)"; }}>
            <svg width="22" height="22" viewBox="0 0 24 24" fill="#e8e0f0"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-4.714-6.231-5.401 6.231H2.744l7.73-8.835L1.254 2.25H8.08l4.253 5.622L18.244 2.25zm-1.161 17.52h1.833L7.084 4.126H5.117L17.083 19.77z"/></svg>
          </a>
          <a href="#" target="_blank" rel="noreferrer" title="Instagram"
            style={{ display: "flex", alignItems: "center", justifyContent: "center", flex: 1, padding: "12px 8px", borderRadius: 10, background: "rgba(225,48,108,.08)", border: "1px solid rgba(225,48,108,.22)", textDecoration: "none", transition: ".15s" }}
            onMouseEnter={e => { e.currentTarget.style.background = "rgba(225,48,108,.18)"; e.currentTarget.style.transform = "translateY(-2px)"; }}
            onMouseLeave={e => { e.currentTarget.style.background = "rgba(225,48,108,.08)"; e.currentTarget.style.transform = "translateY(0)"; }}>
            <svg width="22" height="22" viewBox="0 0 24 24" fill="url(#ig-grad2)"><defs><linearGradient id="ig-grad2" x1="0%" y1="100%" x2="100%" y2="0%"><stop offset="0%" stopColor="#f09433"/><stop offset="25%" stopColor="#e6683c"/><stop offset="50%" stopColor="#dc2743"/><stop offset="75%" stopColor="#cc2366"/><stop offset="100%" stopColor="#bc1888"/></linearGradient></defs><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838a6.162 6.162 0 1 0 0 12.324 6.162 6.162 0 0 0 0-12.324zM12 16a4 4 0 1 1 0-8 4 4 0 0 1 0 8zm6.406-11.845a1.44 1.44 0 1 0 0 2.881 1.44 1.44 0 0 0 0-2.881z"/></svg>
          </a>
        </div>
      </div>
    </aside>
  );
}